import { MutableRefObject, useRef, useState } from "react";
import uniq from "lodash/uniq";
import { Throwable } from "@wss/error-tracking/throwable";
import {
  Accessory,
  GroupingAttributes,
  GroupMember,
  OutletAttributes,
  Product,
} from "~/common/Store/models/ProductListing";
import ValidationError from "../ValidationError";
import {
  minimumQuantity,
  valid,
  mustBuy as mustBuyValidation,
} from "../validations";
import addToCartRequest from "./request";
import { AddToCartModalNames } from "~/common/AddToCartModal/";
import { AtcAnalyticsProps } from "~/analytics/AddToCart/addToCartAnalytics";
import Notification from "~/common/Notification";
import { ProductListingView } from ".";

export interface AddToCartArgs {
  selectedAccessories?: string[];
  virtualGroupingItemNumber?: string;
  product: Product;
  groupingValue: string | undefined;
  hasAccessories: boolean;
  hasAcknowledgedProductWarning: MutableRefObject<boolean>;
  hasAcknowledgedOutletItem: MutableRefObject<boolean>;
  quantity: number;
  openModals: (modals: AddToCartModalNames[]) => void;
  discountCode: string | undefined;
  gtmAtcProps: AtcAnalyticsProps;
}

export interface AddToCartResultData {
  accessories: readonly Accessory[];
  error: Readonly<ValidationError> | undefined;
  groupingValue: string;
  hasAcknowledgedOutletItem: MutableRefObject<boolean>;
  hasAcknowledgedProductWarning: MutableRefObject<boolean>;
  hasMultipleVGGrouping: boolean;
  itemIsInCart: boolean;
  loadingAccessories: boolean;
  quantity: string;
  setAccessories: React.Dispatch<React.SetStateAction<readonly Accessory[]>>;
  setError: React.Dispatch<
    React.SetStateAction<Readonly<ValidationError> | undefined>
  >;
  setGroupingValue: React.Dispatch<string>;
  setItemIsInCart: React.Dispatch<boolean>;
  setLoadingAccessories: React.Dispatch<boolean>;
  setQuantity: React.Dispatch<string>;
}

export interface ProductBoxType {
  isCarouselProductBox: boolean;
  isCompareWithProductBox: boolean;
  isCustomizeNowProductBox: boolean;
}

interface UseAddToCart {
  product: Product;
}

export const doesAnyGroupMembersHasMustPurchaseMoreThanOne = (
  groupingAttributes: GroupingAttributes
) => groupingAttributes.groupMembers.some((gm) => gm.mustPurchaseMoreThanOne);

export const useAddToCart = ({
  product,
}: UseAddToCart): AddToCartResultData => {
  const { mustPurchaseMoreThanOne, groupingAttributes } = product;

  const [quantity, setQuantity] = useState(
    mustPurchaseMoreThanOne ||
      doesAnyGroupMembersHasMustPurchaseMoreThanOne(groupingAttributes)
      ? ""
      : "1"
  );

  const [loadingAccessories, setLoadingAccessories] = useState(false);

  const [accessories, setAccessories] = useState<readonly Accessory[]>([]);

  const [error, setError] = useState<ValidationError | undefined>();

  const [itemIsInCart, setItemIsInCart] = useState(false);

  const [groupingValue, setGroupingValue] = useState("");

  const hasAcknowledgedProductWarning = useRef(false);

  const hasAcknowledgedOutletItem = useRef(false);

  const [firstGroupMember] = groupingAttributes.groupMembers;

  const uniqueConfigurationOptionNames = uniq(
    firstGroupMember?.configurationOptions.map((option) => option.name)
  );

  const hasMultipleVGGrouping = uniqueConfigurationOptionNames.length > 1;

  return {
    accessories,
    setAccessories,
    loadingAccessories,
    setLoadingAccessories,
    error,
    setError,
    itemIsInCart,
    setItemIsInCart,
    quantity,
    setQuantity,
    groupingValue,
    setGroupingValue,
    hasMultipleVGGrouping,
    hasAcknowledgedProductWarning,
    hasAcknowledgedOutletItem,
  };
};
/**
 * If it's not one of our errors we'll show a user friendly message.
 */
export const getErrorMessage = (error?: Error) => {
  if (!error) return undefined;
  let { message } = error;
  if (error.constructor !== ValidationError) {
    message = "There was an error adding this item to your cart.";
  }
  return message;
};

export const qty = (value: string) => {
  const quantityValue = Number(value);
  return Number.isNaN(quantityValue) ? 0 : quantityValue;
};

export const anyGroupMembersInStock = (
  groupingAttributes: GroupingAttributes
) => groupingAttributes.groupMembers.some((gm) => gm.isInStock);

export const canBeReplaced = (
  isOutlet: boolean,
  isReplacementEligible: boolean
) => {
  return !isOutlet && isReplacementEligible;
};

export const isOutletItem = (outletAttributes: OutletAttributes) =>
  outletAttributes && outletAttributes.isOutlet;

const getDescription = (product: Product, groupingValue?: string) => {
  if (product.grouping) {
    const groupMember = product.groupingAttributes.groupMembers.find(
      (gm) => gm.itemNumber === groupingValue
    );
    if (groupMember) {
      return groupMember.displayName;
    }
  }
  return product.descriptionHtml;
};

const addVgModal = (
  product: Product,
  isVirtualItem: boolean,
  hasMultipleVGGrouping: boolean,
  virtualGroupingItemNumber: string | undefined
) => {
  return (
    product.grouping &&
    isVirtualItem &&
    hasMultipleVGGrouping &&
    anyGroupMembersInStock(product.groupingAttributes) &&
    !virtualGroupingItemNumber
  );
};

const addAccessoryModal = (
  product: Product,
  hasAccessories: boolean,
  selectedAccessories: string[] | undefined
) => {
  return (
    hasAccessories &&
    !isOutletItem(product.outletAttributes) &&
    typeof selectedAccessories === "undefined"
  );
};

const addOutletModal = (
  product: Product,
  hasAcknowledgedOutletItem: MutableRefObject<boolean>
) => {
  return (
    isOutletItem(product.outletAttributes) &&
    !hasAcknowledgedOutletItem?.current
  );
};

const addWarningModal = (
  hasWarning: boolean,
  hasAcknowledgedProductWarning: MutableRefObject<boolean>
) => {
  return hasWarning && !hasAcknowledgedProductWarning?.current;
};

const addModals = (
  product: Product,
  isVirtualItem: boolean,
  hasMultipleVGGrouping: boolean,
  virtualGroupingItemNumber: string | undefined,
  hasAccessories: boolean,
  selectedAccessories: string[] | undefined,
  hasAcknowledgedProductWarning: MutableRefObject<boolean>,
  hasAcknowledgedOutletItem: MutableRefObject<boolean>,
  hasWarning: boolean
) => {
  const modalsToOpen: AddToCartModalNames[] = [];

  if (
    addVgModal(
      product,
      isVirtualItem,
      hasMultipleVGGrouping,
      virtualGroupingItemNumber
    )
  ) {
    modalsToOpen.push("virtualGrouping");
  }

  if (addAccessoryModal(product, hasAccessories, selectedAccessories)) {
    modalsToOpen.push("accessories");
  }

  if (addOutletModal(product, hasAcknowledgedOutletItem)) {
    modalsToOpen.push("outlet");
  }

  if (addWarningModal(hasWarning, hasAcknowledgedProductWarning)) {
    modalsToOpen.push("warning");
  }

  return modalsToOpen;
};

export const addToCart = async ({
  selectedAccessories,
  virtualGroupingItemNumber,
  product,
  groupingValue,
  hasAccessories,
  hasAcknowledgedProductWarning,
  hasAcknowledgedOutletItem,
  quantity,
  openModals,
  discountCode,
  gtmAtcProps,
}: AddToCartArgs): Promise<Throwable | void> => {
  const hasWarning =
    !!product?.warningContent && product.warningContent.length > 0;
  const isVirtualItem = product.groupingAttributes.isVirtual;

  const [firstGroupMember] = product.groupingAttributes.groupMembers;
  const uniqueConfigurationOptionNames = uniq(
    firstGroupMember?.configurationOptions.map((option) => option.name)
  );
  const hasMultipleVGGrouping = uniqueConfigurationOptionNames.length > 1;

  const validResult = valid({
    product,
    quantity,
    groupingValue,
  });

  if (validResult.isError) {
    return { isError: true, error: validResult.error };
  }

  // Setup for handling multiple ATC Modals
  if (openModals) {
    const modalsToOpen = addModals(
      product,
      isVirtualItem,
      hasMultipleVGGrouping,
      virtualGroupingItemNumber,
      hasAccessories,
      selectedAccessories,
      hasAcknowledgedProductWarning,
      hasAcknowledgedOutletItem,
      hasWarning
    );

    if (modalsToOpen.length > 0) {
      openModals(modalsToOpen);

      // eslint-disable-next-line consistent-return
      return;
    }
  }

  const selectedProductItemNumber = hasMultipleVGGrouping
    ? virtualGroupingItemNumber
    : groupingValue;

  return addToCartRequest({
    product,
    itemNumber:
      virtualGroupingItemNumber ||
      (product.isEquivalentPart ? product.originalId : product.itemNumber),
    quantity,
    groupingValue,
    accessories: selectedAccessories?.join(","),
    discountCode,
    selectedDescription: getDescription(product, selectedProductItemNumber),
    gtmAtcProps,
  });
};

export const CloseNotificationOnError = (
  error: Readonly<ValidationError> | undefined
) => {
  if (error) {
    Notification.closeAll();
  }
};

export const getButtonStyle = (
  displayRequiredCustomization: boolean,
  hasCustomizableProductsOverride: boolean,
  isOutlet: boolean,
  product: Product,
  productBoxType: ProductBoxType,
  productListingView: ProductListingView,
  width: number
) => {
  if (productBoxType.isCompareWithProductBox) {
    return {
      display: "block",
    };
  }
  if (productListingView === "list" && isOutlet) {
    if (width >= 600) {
      return {};
    }

    return {
      marginLeft: "-30px",
    };
  }
  if (
    productListingView === "grid" &&
    (isOutlet || product.isRequestAQuoteEnabled)
  ) {
    return {
      padding: "0px",
    };
  }
  if (productListingView === "list" && product.isRequestAQuoteEnabled) {
    return {
      maxWidth: "150px",
    };
  }

  if (productListingView === "topProductsCarousel") {
    return {};
  }

  const usesCustomizableButton =
    hasCustomizableProductsOverride || displayRequiredCustomization;

  const shouldNotHavePadding =
    productBoxType.isCarouselProductBox &&
    (product.mustPurchaseMoreThanOne ||
      product.isRequestAQuoteEnabled ||
      usesCustomizableButton);

  if (shouldNotHavePadding) {
    return {
      padding: "0px",
    };
  }

  return {};
};

export const getLastQuantityInterval = (
  startBuy: number,
  mustBuy: number,
  incrementCount: number
) => {
  return startBuy + (incrementCount - 1) * mustBuy;
};

/**
 * If the product is a grouping return the selected grouping image and description.
 * Otherwise just return the standard image and description.
 */
export const getProductImageAndDescription = (
  product: Product,
  groupingValue?: string
) => {
  if (product.grouping) {
    const groupMember = product.groupingAttributes.groupMembers.find(
      (gm) => gm.itemNumber === groupingValue
    );
    if (groupMember)
      return {
        selectedImage: groupMember.primaryImagePath,
        selectedDescription: groupMember.displayName,
      };
  }
  return {
    selectedImage: product.primaryImagePath,
    selectedDescription: product.descriptionHtml,
  };
};

export const getSearchParams = (): Record<string, string> => {
  return Object.fromEntries(new URLSearchParams(window.location.search));
};

export const getItemsFromUrl = (): string[] => {
  const { items } = getSearchParams();
  return items ? items.split(",") : [];
};

export const getMatchingItemId = (
  itemIds: string[],
  product: Product
): string | undefined => {
  const { groupMembers } = product.groupingAttributes;

  const groupMemberItemNumbers = new Set(
    groupMembers.map((member) => member.itemNumber)
  );
  return itemIds.find((item) => groupMemberItemNumbers.has(item));
};

export const getSelectedGroupMember = (
  product: Product,
  groupingValue: string
) => {
  return (
    product.grouping &&
    groupingValue &&
    product.groupingAttributes.groupMembers.find(
      (groupMember) => groupMember.itemNumber === groupingValue
    )
  );
};

export const shouldForceQuantityToOne = (
  inputValue: string,
  isForcingCustomQuantity: boolean,
  lastQuantityInterval: number,
  selectedGroupMember: false | "" | GroupMember | undefined
) => {
  return (
    selectedGroupMember &&
    inputValue === lastQuantityInterval.toString() &&
    !isForcingCustomQuantity
  );
};

export const validateQuantity = (
  quantityValue: string,
  product: Product,
  addToCartData: AddToCartResultData
) => {
  const { error, groupingValue, setError } = addToCartData;

  if (
    error?.source &&
    [minimumQuantity, mustBuyValidation].includes(error.source)
  ) {
    const result = valid({
      product,
      quantity: qty(quantityValue),
      groupingValue,
      validationsToRun: [minimumQuantity, mustBuyValidation],
    });

    if (result.isError) {
      setError(result.error);
    }
  }
};
